import api, { API_BASE_URL } from './api';

function getAiHeaders() {
  try {
    const provider = localStorage.getItem('ai:provider') || 'deepseek';
    const apiKey = localStorage.getItem(`ai:apiKey:${provider}`) || '';
    const model = localStorage.getItem(`ai:model:${provider}`) || (provider==='deepseek' ? 'deepseek-chat' : provider==='spark' ? 'gemma-4-31B-it' : 'claude-3-5-sonnet-latest');
    const headers: Record<string, string> = {
      'x-ai-provider': provider,
      'x-ai-model': model,
    };
    if (apiKey) headers['x-ai-api-key'] = apiKey;
    return headers;
  } catch {
    return {} as any;
  }
}

export type VisionContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } };

export type ChatMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string | VisionContentPart[];
};

export async function chat(
  messages: ChatMessage[],
  opts?: { model?: string; temperature?: number; max_tokens?: number; assistant?: string }
) {
  const headers = getAiHeaders();
  const body = { messages, ...opts } as any;
  if (opts?.model) headers['x-ai-model'] = opts.model;
  const resp = await api.post('/ai/chat', body, { headers });
  return resp.data?.data?.content as string;
}

/** Spark Gemma przez /ai/public/chat — bez JWT; backend musi mieć SPARK_GEMMA_PUBLIC=1. */
/** Agent Spark + MySQL (read-only); wymaga JWT. Przy 503 (brak MYSQL_AGENT_*) można zfallbackować na chatPublic. */
export async function agentChat(
  messages: ChatMessage[],
  opts?: { model?: string; temperature?: number; max_tokens?: number; assistant?: string }
) {
  const headers = getAiHeaders();
  const payload = messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: typeof m.content === 'string' ? m.content : '[wiadomość niewspierana w agencie]',
    }));
  const body = { messages: payload, ...opts } as Record<string, unknown>;
  if (opts?.model) headers['x-ai-model'] = opts.model;
  const resp = await api.post('/ai/agent/chat', body, { headers });
  return resp.data?.data?.content as string;
}

/** Zdarzenia SSE z POST /ai/agent/chat-stream (rzeczywiste kroki agenta). */
export type AgentMysqlStreamEvent =
  | { type: 'started'; maxIterations: number }
  | { type: 'thinking'; iteration: number; maxIterations: number }
  | { type: 'tool_calls'; iteration: number; tools: string[] }
  | { type: 'mysql_read_query'; preview: string; truncated?: boolean }
  | { type: 'mysql_read_query_done'; ok: boolean; message?: string; sqlCalls?: number }
  | { type: 'read_context_file'; path: string; preview: string; truncated?: boolean }
  | { type: 'read_context_file_done'; ok: boolean; path?: string; message?: string; contextFileReads?: number }
  | { type: 'kzm_quick_create'; preview: string }
  | {
      type: 'kzm_quick_create_done';
      ok: boolean;
      httpStatus?: number;
      message?: string;
      kzmQuickCreates?: number;
    }
  | { type: 'kzm_staging_upload'; count: number }
  | {
      type: 'kzm_staging_upload_done';
      ok: boolean;
      fileIds?: string[];
      suggestedDescriptions?: string[];
      message?: string;
    }
  | { type: 'unknown_tool'; name?: string }
  /** Postęp po stronie serwera (np. odczyt PDF przed wywołaniem Gemmy). */
  | { type: 'status'; phase?: string; message?: string }
  /** Fragment treści z upstreamu (OpenAI stream: true); UI dokleja do bąbelka asystenta. */
  | { type: 'assistant_delta'; text: string }
  /** Model przeszedł do narzędzi — wyczyść prowizoryczny tekst z bieżącej rundy. */
  | { type: 'assistant_stream_reset' }
  | {
      type: 'complete';
      content: string;
      sqlCalls: number;
      contextFileReads?: number;
      kzmQuickCreates?: number;
      /** np. `length` gdy model osiągnął max_tokens (ucięta odpowiedź). */
      finish_reason?: string | null;
      /** Import z kolejki: merge + status „done” wykonane już na backendzie (nie duplikować wierszy w UI). */
      queueFinishedOnServer?: boolean;
    }
  | { type: 'error'; message: string; code?: string; status?: number; detail?: string };

function buildAgentChatPayload(
  messages: ChatMessage[],
  opts?: { model?: string; temperature?: number; max_tokens?: number; assistant?: string }
) {
  const payload = messages
    .filter((m) => m.role === 'user' || m.role === 'assistant')
    .map((m) => ({
      role: m.role as 'user' | 'assistant',
      content: typeof m.content === 'string' ? m.content : '[wiadomość niewspierana w agencie]',
    }));
  return { messages: payload, ...opts } as Record<string, unknown>;
}

/**
 * Agent z podglądem kroków (jak w Cursorze): thinking, mysql_read_query itd.
 * Wywołuje onEvent dla każdej ramki SSE; zwraca treść z zdarzenia `complete`.
 */
export async function agentChatStream(
  messages: ChatMessage[],
  opts: { model?: string; temperature?: number; max_tokens?: number; assistant?: string } | undefined,
  onEvent: (ev: AgentMysqlStreamEvent) => void
): Promise<string> {
  const headers = getAiHeaders();
  if (opts?.model) headers['x-ai-model'] = opts.model;
  let token: string | null = null;
  try {
    token = localStorage.getItem('auth_token');
  } catch {
    token = null;
  }
  const body = buildAgentChatPayload(messages, opts);
  const res = await fetch(`${API_BASE_URL}/ai/agent/chat-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {
      try {
        msg = await res.text();
      } catch {
        /* noop */
      }
    }
    throw new Error(msg);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Brak treści odpowiedzi (stream)');

  const decoder = new TextDecoder();
  let buffer = '';
  let finalContent = '';
  /** Oddajemy wątek UI — bez tego setki zdarzeń SSE z rzędu blokują interakcję całej aplikacji. */
  let deltaYieldCounter = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      const line = block.trim();
      if (!line.startsWith('data:')) continue;
      const jsonStr = line.slice(5).trim();
      if (!jsonStr) continue;
      let ev: AgentMysqlStreamEvent;
      try {
        ev = JSON.parse(jsonStr) as AgentMysqlStreamEvent;
      } catch {
        continue;
      }
      onEvent(ev);
      if (ev.type === 'complete') finalContent = ev.content || '';
      if (ev.type === 'error') throw new Error(ev.message || 'Błąd agenta');
      if (ev.type === 'assistant_delta') {
        deltaYieldCounter += 1;
        if (deltaYieldCounter % 20 === 0) {
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });
        }
      }
    }
  }

  if (!finalContent) throw new Error('Brak odpowiedzi Gemmy (stream zakończony bez treści)');
  return finalContent;
}

/**
 * Agent z multipart: `messages` jako JSON + `files[]` (PDF). Backend wgrywa PDF na Drive (staging) i dopisuje ID do kontekstu.
 */
export async function agentChatStreamWithFiles(
  messages: ChatMessage[],
  files: File[],
  opts: { model?: string; temperature?: number; max_tokens?: number; assistant?: string } | undefined,
  onEvent: (ev: AgentMysqlStreamEvent) => void
): Promise<string> {
  const headers = getAiHeaders();
  if (opts?.model) headers['x-ai-model'] = opts.model;
  let token: string | null = null;
  try {
    token = localStorage.getItem('auth_token');
  } catch {
    token = null;
  }
  const payload = buildAgentChatPayload(messages, opts);
  const form = new FormData();
  form.append('messages', JSON.stringify(payload.messages));
  if (typeof payload.model === 'string') form.append('model', payload.model);
  if (typeof payload.temperature === 'number') form.append('temperature', String(payload.temperature));
  if (typeof payload.max_tokens === 'number') form.append('max_tokens', String(payload.max_tokens));
  if (typeof payload.assistant === 'string') form.append('assistant', payload.assistant);
  for (const f of files) {
    form.append('files', f);
  }

  const res = await fetch(`${API_BASE_URL}/ai/agent/chat-stream-with-files`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: form,
    credentials: 'include',
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {
      try {
        msg = await res.text();
      } catch {
        /* noop */
      }
    }
    throw new Error(msg);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Brak treści odpowiedzi (stream)');

  const decoder = new TextDecoder();
  let buffer = '';
  let finalContent = '';
  let deltaYieldCounter = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      const line = block.trim();
      if (!line.startsWith('data:')) continue;
      const jsonStr = line.slice(5).trim();
      if (!jsonStr) continue;
      let ev: AgentMysqlStreamEvent;
      try {
        ev = JSON.parse(jsonStr) as AgentMysqlStreamEvent;
      } catch {
        continue;
      }
      onEvent(ev);
      if (ev.type === 'complete') finalContent = ev.content || '';
      if (ev.type === 'error') throw new Error(ev.message || 'Błąd agenta');
      if (ev.type === 'assistant_delta') {
        deltaYieldCounter += 1;
        if (deltaYieldCounter % 20 === 0) {
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });
        }
      }
    }
  }

  if (!finalContent) throw new Error('Brak odpowiedzi Gemmy (stream zakończony bez treści)');
  return finalContent;
}

/**
 * Stream z `/ai/przedmiar/pdf-extract-stream` — Gemma (Spark) + tekst z PDF, bez agenta MySQL i bez promptu czatu @gemma.
 * Zdarzenia jak `agentChatStream` (`assistant_delta`, `complete`, `error`).
 */
export async function przedmiarPdfExtractStream(
  files: File[],
  onEvent: (ev: AgentMysqlStreamEvent) => void,
  opts?: { gemmaQueueJobId?: string }
): Promise<string> {
  const headers: Record<string, string> = { ...getAiHeaders() };
  headers['x-ai-provider'] = 'spark';
  let token: string | null = null;
  try {
    token = localStorage.getItem('auth_token');
  } catch {
    token = null;
  }
  const form = new FormData();
  for (const f of files) {
    form.append('files', f);
  }
  const qid = opts?.gemmaQueueJobId?.trim();
  if (qid) form.append('gemmaQueueJobId', qid);

  const res = await fetch(`${API_BASE_URL}/ai/przedmiar/pdf-extract-stream`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: form,
    credentials: 'include',
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) msg = j.message;
    } catch {
      try {
        msg = await res.text();
      } catch {
        /* noop */
      }
    }
    throw new Error(msg);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Brak treści odpowiedzi (stream)');

  const decoder = new TextDecoder();
  let buffer = '';
  let finalContent = '';
  let streamedAcc = '';
  let deltaYieldCounter = 0;

  const dispatchOneSsePayload = async (payloadLine: string) => {
    const line = payloadLine.trim();
    if (!line.startsWith('data:')) return;
    const jsonStr = line.slice(5).trim();
    if (!jsonStr || jsonStr === '[DONE]') return;
    let ev: AgentMysqlStreamEvent;
    try {
      ev = JSON.parse(jsonStr) as AgentMysqlStreamEvent;
    } catch {
      return;
    }
    onEvent(ev);
    if (ev.type === 'complete') finalContent = ev.content || '';
    if (ev.type === 'error') {
      const tech = 'detail' in ev && typeof (ev as { detail?: string }).detail === 'string' ? (ev as { detail?: string }).detail : '';
      const base = ev.message || 'Błąd ekstrakcji';
      throw new Error(tech && tech !== base ? `${base}\n\nSzczegół techniczny: ${tech}` : base);
    }
    if (ev.type === 'assistant_delta') {
      if (ev.text) streamedAcc += ev.text;
      deltaYieldCounter += 1;
      if (deltaYieldCounter % 20 === 0) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
      }
    }
  };

  while (true) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (readErr: unknown) {
      const re = readErr as { message?: string; name?: string };
      const m = re?.message || String(readErr);
      throw new Error(
        `Odczyt strumienia odpowiedzi przerwany (${re?.name || 'błąd'}): ${m}. ` +
          'Typowe przyczyny: zamknięcie karty, utrata sieci lub timeout pośrednika — fragmenty odpowiedzi mogą być w podglądzie.'
      );
    }
    const { done, value } = chunk;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      await dispatchOneSsePayload(block);
    }
  }

  /** Ostatnia ramka często nie kończy się `\n\n` — proxy/nginx; bez flush tracimy `complete` i pełny JSON. */
  if (buffer.trim()) {
    for (const piece of buffer.split(/\r?\n/)) {
      await dispatchOneSsePayload(piece);
    }
  }

  if (!finalContent) {
    if (streamedAcc.trim()) {
      throw new Error(
        'Strumień zakończył się bez ramki „complete” — JSON jest prawdopodobnie niepełny (ucięty w trakcie generacji). ' +
          'Często winny jest timeout reverse-proxy (np. nginx), limit czasu backendu (AI_PRZEDMIAR_PDF_STREAM_TIMEOUT_MS) lub zerwane połączenie z serwerem modelu. ' +
          `Zebrano ok. ${streamedAcc.length} znaków tekstu.`
      );
    }
    throw new Error('Brak odpowiedzi Gemmy (stream zakończony bez treści)');
  }
  return finalContent;
}

export async function chatPublic(
  messages: ChatMessage[],
  opts?: { model?: string; temperature?: number; max_tokens?: number; assistant?: string }
) {
  let model = opts?.model;
  try {
    if (!model) model = localStorage.getItem('ai:model:spark') || 'gemma-4-31B-it';
  } catch {
    model = model || 'gemma-4-31B-it';
  }
  const body = { messages, ...opts, model } as any;
  const headers: Record<string, string> = {};
  if (model) headers['x-ai-model'] = model;
  const resp = await api.post('/ai/public/chat', body, { headers });
  return resp.data?.data?.content as string;
}

export async function askDatabase(query: string, project_id?: string) {
  try {
    const headers = getAiHeaders();
    const resp = await api.post('/ai/ask', { query, project_id }, { headers });
    return resp.data?.data?.content as string;
  } catch (e: any) {
    const msg = e?.response?.data?.message || e?.message || 'Błąd zapytania do bazy';
    throw new Error(msg);
  }
}

export async function chatWithFiles(input: {
  messages?: ChatMessage[];
  prompt?: string;
  files?: File[];
  model?: string;
  temperature?: number;
  max_tokens?: number;
  assistant?: string;
}) {
  const form = new FormData();
  if (input.messages) form.append('messages', JSON.stringify(input.messages));
  if (input.prompt) form.append('prompt', input.prompt);
  if (typeof input.model === 'string') form.append('model', input.model);
  if (typeof input.temperature === 'number') form.append('temperature', String(input.temperature));
  if (typeof input.max_tokens === 'number') form.append('max_tokens', String(input.max_tokens));
  if (typeof input.assistant === 'string') form.append('assistant', input.assistant);
  const list = input.files || [];
  list.forEach((f) => form.append('files', f));
  const headers = getAiHeaders();
  if (input.model) headers['x-ai-model'] = input.model;
  const resp = await api.post('/ai/chat-with-files', form, { headers });
  return resp.data?.data?.content as string;
}

export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
